import React, { useState } from 'react';
import { ArrowLeft, ShoppingCart, User, Heart, Menu, X, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template8 = () => {
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navLinks = [
        { name: 'Home', path: '/template8' },
        { name: 'Shop', path: '/template8/shop' },
        { name: 'Contact', path: '/template8/contact' },
        { name: 'Orders', path: '/template8/orders' }
    ];

    return (
        <div className="bg-white min-h-screen font-sans">
                        {/* Header with Navigation */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="flex justify-between items-center py-4 lg:py-5">
                        <h1 onClick={() => navigate('/template8')} className="text-2xl lg:text-3xl font-bold text-gray-900 cursor-pointer tracking-tight">
                            STYLE STUDIO
                        </h1>
                        <div className="flex items-center gap-4 lg:gap-6">
                            <Search className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <Heart className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template8/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">0</span>
                            </div>
                            <User className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" onClick={() => navigate('/template8/profile')} />
                        </div>
                    </div>
                    <nav className="border-t border-gray-200">
                        <div className="flex justify-center items-center gap-8 lg:gap-12 py-4">
                            <button onClick={() => navigate('/template8')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Home</button>
                            <button onClick={() => navigate('/template8/shop')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Shop</button>
                            <button onClick={() => navigate('/template8/contact')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Contact</button>
                            <button onClick={() => navigate('/template8/orders')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Orders</button>
                        </div>
                    </nav>
                </div>
            </header>
            
            {/* Hero Section - Replicated from DesignerPage8 */}
            <div className="relative flex flex-col lg:flex-row items-center justify-center min-h-screen bg-gray-50 text-gray-900 p-8 lg:p-12">
                <div className="absolute inset-0 bg-grid-gray-200 [mask-image:linear-gradient(to_bottom,white,transparent)]"></div>
                <div className="relative z-10 w-full lg:w-1/2 flex flex-col items-center lg:items-start text-center lg:text-left mb-12 lg:mb-0">
                    <h1 className="text-5xl md:text-7xl font-serif tracking-tight">Vintage Charm</h1>
                    <p className="mt-6 text-lg max-w-lg text-gray-600">
                        An exclusive collection of finely crafted designs, where tradition meets contemporary elegance.
                    </p>
                    <button
                        onClick={() => navigate('/template8/shop')}
                        className="mt-8 inline-block bg-gray-900 text-white font-semibold py-3 px-8 rounded-full hover:bg-gray-800 transition-colors duration-300"
                    >
                        Explore The Collection
                    </button>
                </div>
                <div className="relative z-10 w-full md:w-3/4 lg:w-1/2 h-[60vh] lg:h-[70vh]">
                    <div className="relative h-full w-full shadow-2xl rounded-lg overflow-hidden">
                        <img
                            src="https://placehold.co/1200x800/E0E0E0/333333?text=Vintage+Collection"
                            alt="Hero Image" 
                            className="absolute w-full h-full object-cover" 
                        />
                        <div className="absolute inset-0 bg-black/10"></div>
                    </div>
                </div>
            </div>

            {/* About Section - Replicated from DesignerPage8 */}
            <div className="bg-white py-24 sm:py-32">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
                    <p className="text-base font-semibold leading-7 text-gray-600">The Vision</p>
                    <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                        Crafting Tomorrow's Heirlooms
                    </h2>
                    <p className="mt-6 text-lg leading-8 text-gray-600">
                        Inspired by the rich history of fashion, our designs blend timeless silhouettes with modern details. We believe in creating pieces that are not just clothes, but cherished memories waiting to be made.
                    </p>
                </div>
            </div>

            {/* Featured Collections Section */}
            <div className="bg-gray-50 py-24 sm:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-2xl mx-auto text-center mb-16">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Shop By Category</h2>
                        <p className="mt-4 text-lg text-gray-600">
                            Discover curated selections from our latest lookbooks and signature lines.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
                        {[1, 2, 3, 4].map((id, index) => (
                            <button
                                key={index}
                                onClick={() => navigate('/template8/shop')}
                                className="block group text-left"
                            >
                                <div className="overflow-hidden rounded-lg bg-gray-100 shadow-lg">
                                    <img src={`https://placehold.co/400x500/E0E0E0/333333?text=Collection+${id}`} alt={`Collection ${id}`} className="w-full object-cover aspect-[4/5] group-hover:scale-105 transition-transform duration-500 ease-in-out" />
                                </div>
                                <h3 className="mt-4 text-xl font-semibold text-gray-900">Collection Name {id}</h3>
                                <p className="mt-1 text-base text-gray-600">Curated styles for you.</p>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Product Grid */}
            <section className="w-full bg-gray-50 py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-4">All Products</h2>
                        <p className="text-xl text-gray-600">Discover our latest collection</p>
                    </div>

                    <div className="w-full max-w-none">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(id => (
                                <div key={id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-2xl hover:-translate-y-2 hover:border-gray-900 transition-all duration-300 group">
                                    <div
                                        className="aspect-[4/5] overflow-hidden relative bg-gray-50 cursor-pointer"
                                        onClick={() => navigate(`/template8/product/${id}`)}
                                    >
                                        <img
                                            src={`https://placehold.co/400x500/F0F0F0/333333?text=Product+${id}`}
                                            alt={`Product ${id}`}
                                            className="object-cover object-center w-full h-full group-hover:scale-110 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="p-4 lg:p-6">
                                        <h3
                                            className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 cursor-pointer hover:text-gray-700"
                                            onClick={() => navigate(`/template8/product/${id}`)}
                                        >
                                            Product Title {id}
                                        </h3>
                                        <p className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">${(69 + id * 10)}.00</p>
                                        <button
                                            onClick={() => navigate('/template8/cart')}
                                            className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-all duration-300"
                                        >
                                            Add to Cart
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

                        {/* Footer */}
            <footer className="bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">STYLE STUDIO</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template8')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template8/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template8/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template8/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@stylestudio.com</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">© 2025 STYLE STUDIO. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template8;